import { Image } from "expo-image";
import { type ImageSourcePropType } from "react-native";
import { type ColorTokens, View, YStack } from "tamagui";

import ForbesDark from "@/assets/images/landing_page/general_care/forbes-dark.png";
import ForbesLight from "@/assets/images/landing_page/general_care/forbes-light.png";
import WapoDark from "@/assets/images/landing_page/general_care/wapo_dark.png";
import WapoLight from "@/assets/images/landing_page/general_care/wapo_light.png";
import WhiteHouseLogoDark from "@/assets/images/landing_page/general_care/white-house-logo-dark.png";
import WhiteHouseLogoLight from "@/assets/images/landing_page/general_care/white-house-logo-light.png";
import { Grid, getGridLayout } from "@/components";
import { Text } from "@/components/Typography";
import { useIsDarkTheme } from "@/theme/helpers";

type TestimonialItem = {
  type: "testimonial";
  id: string;
  tag: string;
  quote: string;
  author: string;
  location: string;
  tagColor: ColorTokens;
};

type PressItem = {
  type: "press";
  id: string;
  label: string;
  image: ImageSourcePropType | null;
  imageDark?: ImageSourcePropType;
};

type ColumnItem = TestimonialItem | PressItem;

const COLUMNS: ColumnItem[][] = [
  [
    {
      type: "testimonial",
      id: "neal",
      tag: "Primary care patient",
      quote:
        "This service is terrific! As a travel RN, I really appreciate having access to friendly, high-quality primary care at my finger tips. I regularly recommend Curai to my nurse colleagues. Thank you",
      author: "Neal F.",
      location: "MN",
      tagColor: "$fillBadgePrimary",
    },
    {
      type: "press",
      id: "forbes",
      label: "Featured in",
      image: ForbesLight,
      imageDark: ForbesDark,
    },
  ],
  [
    {
      type: "press",
      id: "nejm",
      label: "Featured in",
      image: WapoLight,
      imageDark: WapoDark,
    },
    {
      type: "testimonial",
      id: "nicole",
      tag: "Chronic care patient",
      quote:
        "Everything went great the Doctor was very informative and helpful. It made life so much easier not having to go to dr office with us being like we are.",
      author: "Nicole W.",
      location: "OH",
      tagColor: "$fillBadgeCurai",
    },
  ],
  [
    {
      type: "testimonial",
      id: "kimberly",
      tag: "Urgent care patient",
      quote:
        "I really felt like there was a real concern about me and my issues and getting to the bottom of my symptoms as well as my diagnosis.",
      author: "Kimberly P.",
      location: "OK",
      tagColor: "$fillBadgeTertiary",
    },
    {
      type: "press",
      id: "whitehouse",
      label: "In partnership with",
      image: WhiteHouseLogoLight,
      imageDark: WhiteHouseLogoDark,
    },
  ],
];

function TagChip({
  label,
  tagColor,
}: {
  label: string;
  tagColor: ColorTokens;
}) {
  return (
    <View
      backgroundColor={tagColor}
      borderRadius={999}
      paddingHorizontal="$200"
      paddingVertical="$75"
      alignSelf="flex-start"
    >
      <Text size="$xs">{label}</Text>
    </View>
  );
}

function TestimonialCard({ item }: { item: TestimonialItem }) {
  return (
    <YStack
      flexGrow={1}
      flexShrink={0}
      flexBasis="auto"
      backgroundColor="$backgroundGround"
      borderRadius="$200"
      borderWidth="$strokeWidth"
      borderColor="$fillNeutral"
      padding="$300"
      gap="$300"
      minWidth={0}
    >
      <TagChip label={item.tag} tagColor={item.tagColor} />
      <Text color="$textSecondary" flexShrink={1}>
        {item.quote}
      </Text>
      <YStack marginTop="auto" gap="$50" minWidth={0}>
        <Text semibold flexShrink={1}>
          {item.author}, {item.location}
        </Text>
        <Text size="$xs" color="$textSecondary" flexShrink={1}>
          Verified patient
        </Text>
      </YStack>
    </YStack>
  );
}

function PressCard({ item }: { item: PressItem }) {
  const isDark = useIsDarkTheme();
  const logo = isDark ? (item.imageDark ?? item.image) : item.image;

  return (
    <YStack
      flexGrow={1}
      flexShrink={0}
      flexBasis="auto"
      backgroundColor="$backgroundGround"
      borderRadius="$200"
      borderWidth="$strokeWidth"
      borderColor="$fillNeutral"
      padding="$300"
      paddingVertical="$500"
      alignItems="center"
      justifyContent="center"
      gap="$100"
    >
      <Text size="$sm" color="$textPrimary">
        {item.label}
      </Text>
      {logo ? (
        <Image
          source={logo}
          style={{ width: 160, height: 60 }}
          contentFit="contain"
        />
      ) : (
        <View
          width={160}
          height={60}
          backgroundColor="$fillNeutral"
          borderRadius="$100"
        />
      )}
    </YStack>
  );
}

function ColumnCard({ item }: { item: ColumnItem }) {
  if (item.type === "press") return <PressCard item={item} />;
  return <TestimonialCard item={item} />;
}

export default function PatientTestimonials() {
  return (
    <YStack
      maxWidth="$landingPageConstraint"
      alignSelf="center"
      backgroundColor="$backgroundGround"
      paddingHorizontal="$400"
      paddingVertical="$800"
      gap="$600"
      alignItems="center"
      width="100%"
    >
      <YStack alignItems="center" gap="$100">
        <Text size="$xxl" textAlign="center">
          What our patients are saying
        </Text>
        <Text size="$md" textAlign="center" color="$textSecondary">
          Real people. Real visits. Real outcomes.
        </Text>
      </YStack>

      <Grid alignItems="stretch" $lg={{ maxWidth: "100%" }}>
        {COLUMNS.map((column) => (
          <YStack
            key={column[0].id}
            width="100%"
            $lg={getGridLayout(4).$lg}
            gap="$300"
          >
            {column.map((item) => (
              <ColumnCard key={item.id} item={item} />
            ))}
          </YStack>
        ))}
      </Grid>
    </YStack>
  );
}
